import React, { useState } from 'react';
import { ChevronDown, ListChecks, Loader2 } from 'lucide-react';
import { fmtInt, fmtMinutes } from './starFormat';

export const HostDailyStatement = ({
  statement, title = 'Extrato diário do ciclo', validDaysNote = 'pela KAKO',
  onOpen, loading, error, topNote, testIdPrefix = 'host-statement',
}) => {
  const [open, setOpen] = useState(false);

  if (!statement && !onOpen) {
    return (
      <div data-testid={`${testIdPrefix}-unavailable`}
        className="rounded-2xl border border-white/[0.07] bg-white/[0.03] p-3.5 text-xs text-zinc-400 backdrop-blur-md">
        A KAKO não devolveu o detalhe dia a dia agora. Tente novamente mais tarde.
      </div>
    );
  }

  const toggle = () => {
    setOpen((v) => !v);
    if (!open && onOpen) onOpen();
  };

  const t = statement?.totals;
  return (
    <div className="overflow-hidden rounded-3xl border border-white/[0.07] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)]"
      style={{ background: 'linear-gradient(145deg,#0D111A 0%,#141B28 100%)' }}>
      <button type="button" onClick={toggle}
        data-testid={`${testIdPrefix}-toggle-btn`}
        className="flex w-full items-center justify-between gap-2 p-4 text-left transition-colors duration-300 hover:bg-white/[0.03]">
        <span className="flex items-center gap-2.5 font-display text-sm font-bold text-white">
          <ListChecks className="h-4 w-4 text-amber-400" /> {title}
        </span>
        <span className="flex items-center gap-2">
          {t && (
            <span className="rounded-full border border-white/10 bg-white/[0.05] px-2.5 py-0.5 font-mono text-[10px] font-bold text-zinc-300">
              {fmtInt(t.valid_days)} válido(s)
            </span>
          )}
          <ChevronDown className={`h-4 w-4 text-zinc-500 ${open ? 'rotate-180' : ''}`}
            style={{ transition: 'transform 0.3s cubic-bezier(0.16,1,0.3,1)' }} />
        </span>
      </button>

      {open && (
        <div data-testid={`${testIdPrefix}-collapsible-content`} className="border-t border-white/[0.06]">
          {topNote && (
            <p className="border-b border-white/[0.06] px-4 py-3 text-[10px] leading-relaxed text-zinc-400"
              data-testid={`${testIdPrefix}-top-note`}>
              {topNote}
            </p>
          )}

          {loading && (
            <p className="flex items-center gap-2 px-4 py-6 text-xs text-zinc-400"
              data-testid={`${testIdPrefix}-loading`}>
              <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> Buscando na KAKO...
            </p>
          )}
          {!loading && error && (
            <p className="px-4 py-6 text-xs text-amber-300" data-testid={`${testIdPrefix}-error`}>{error}</p>
          )}

          {!loading && statement && (
            <>
              <div className="divide-y divide-white/[0.05]" data-testid={`${testIdPrefix}-table`}>
                {statement.days.map((d) => (
                  <div key={d.iso} data-testid={`${testIdPrefix}-day-${d.iso}`}
                    className={`flex items-center gap-3 px-4 py-2.5 ${d.no_data ? 'opacity-60' : ''}`}>
                    <span className="w-11 shrink-0 font-mono text-xs font-bold text-zinc-300">{d.date}</span>

                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className={`font-mono text-xs font-bold ${
                          d.video_minutes > 0 ? 'text-emerald-300' : 'text-zinc-400'}`}>
                          {fmtMinutes(d.video_minutes)}
                        </span>
                        <span className="text-[10px] text-zinc-600">vídeo</span>
                        {d.audio_minutes > 0 && (
                          <>
                            <span className="font-mono text-[10px] text-zinc-600 line-through">
                              {fmtMinutes(d.audio_minutes)} áudio
                            </span>
                            <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-600">
                              não conta
                            </span>
                          </>
                        )}
                      </p>
                      {d.note && <p className="mt-0.5 text-[10px] leading-tight text-zinc-500">{d.note}</p>}
                    </div>

                    <span className="shrink-0 text-right">
                      <span className="block font-mono text-xs font-bold text-white">
                        {d.commission === null ? 'sem dado' : fmtInt(d.commission)}
                      </span>
                      <span className="text-[9px] uppercase tracking-wider text-zinc-600">cristais</span>
                    </span>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-2 border-t border-white/[0.06] bg-black/30 p-3 text-center">
                <span data-testid={`${testIdPrefix}-footer-valid-days`}>
                  <span className="block text-[9px] uppercase tracking-[0.14em] text-zinc-500">Dias válidos</span>
                  <b className="font-mono text-sm text-white">{fmtInt(t.valid_days)}</b>
                  <span className="block text-[8px] text-zinc-600">{validDaysNote}</span>
                </span>
                <span data-testid={`${testIdPrefix}-footer-total-crystals`}>
                  <span className="block text-[9px] uppercase tracking-[0.14em] text-zinc-500">Soma das linhas</span>
                  <b className="font-mono text-sm text-white">{fmtInt(t.commission)}</b>
                </span>
                <span data-testid={`${testIdPrefix}-footer-avg`}>
                  <span className="block text-[9px] uppercase tracking-[0.14em] text-zinc-500">Média por dia válido</span>
                  <b className="font-mono text-sm text-white">{fmtInt(t.avg_per_day)}</b>
                </span>
              </div>

              {t.commission_undetailed > 0 && (
                <p className="border-t border-white/[0.06] px-4 py-3 text-[10px] leading-relaxed text-zinc-500"
                  data-testid={`${testIdPrefix}-undetailed-note`}>
                  A KAKO fecha o ciclo em <span className="font-mono text-zinc-400">{fmtInt(t.commission_kako)}</span>{' '}
                  cristais de comissão e detalha dia a dia apenas os dias com live. A diferença de{' '}
                  <span className="font-mono text-zinc-400">{fmtInt(t.commission_undetailed)}</span> cristais veio
                  de presentes em dias sem live registrada por ela.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
